/**
 * Workspace store: a browser-based "local folder" for Quanta.
 *
 * A workspace is a real directory the user picks with the File System Access
 * API. Files the calculator saves (via save/csvwrite/filewrite in calcscript,
 * or the Files panel) are written straight into that folder, so they are real
 * files on disk the user can open in other apps and share with others.
 *
 * The picked directory handle is persisted in IndexedDB so the workspace
 * survives reloads (re-granting permission needs one click after a reload).
 *
 * Browsers without the File System Access API (Firefox/Safari) transparently
 * fall back to a localStorage-backed virtual folder; import/export in the
 * Files panel still lets those users move files in and out for sharing.
 */
import { create } from "zustand";

export interface WsFile {
  name: string;
  size: number;
}

type PermState = "granted" | "prompt" | "denied" | "none";

// Minimal typings for the File System Access API (not in the default TS lib).
type FsPermOpts = { mode?: "read" | "readwrite" };
interface FsFileHandle {
  kind: "file";
  name?: string;
  getFile(): Promise<File>;
  createWritable(): Promise<{ write(data: string): Promise<void>; close(): Promise<void> }>;
}
type FsPickerType = { description?: string; accept: Record<string, string[]> };
interface FsDirHandle {
  kind: "directory";
  name: string;
  getFileHandle(name: string, opts?: { create?: boolean }): Promise<FsFileHandle>;
  removeEntry(name: string, opts?: { recursive?: boolean }): Promise<void>;
  entries(): AsyncIterableIterator<[string, FsFileHandle | FsDirHandle]>;
  queryPermission(opts?: FsPermOpts): Promise<PermState>;
  requestPermission(opts?: FsPermOpts): Promise<PermState>;
}
declare global {
  interface Window {
    showDirectoryPicker?: (opts?: { mode?: "read" | "readwrite" }) => Promise<FsDirHandle>;
    showSaveFilePicker?: (opts?: {
      suggestedName?: string;
      types?: FsPickerType[];
    }) => Promise<FsFileHandle>;
    showOpenFilePicker?: (opts?: {
      multiple?: boolean;
      types?: FsPickerType[];
    }) => Promise<FsFileHandle[]>;
  }
}

export const fsSupported =
  typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";

/**
 * Save text to a location the user picks on their local file system. Uses the
 * native "Save As" dialog where available, otherwise falls back to a plain
 * download (which lands in the browser's Downloads folder). Returns the saved
 * file name, or null if the user cancelled.
 */
export async function saveTextToLocalFile(
  suggestedName: string,
  content: string,
  accept?: Record<string, string[]>,
): Promise<string | null> {
  if (typeof window !== "undefined" && typeof window.showSaveFilePicker === "function") {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName,
        types: accept ? [{ accept }] : undefined,
      });
      const w = await handle.createWritable();
      await w.write(content);
      await w.close();
      return handle.name || suggestedName;
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return null;
      // Any other error: fall through to the download fallback.
    }
  }
  const url = URL.createObjectURL(new Blob([content], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return suggestedName;
}

/**
 * Open a single text file from the user's local file system. Uses the native
 * open dialog where available, otherwise a hidden file input. Returns the file
 * name and its text, or null if the user cancelled.
 */
export async function openTextFromLocalFile(
  accept?: Record<string, string[]>,
): Promise<{ name: string; content: string } | null> {
  if (typeof window !== "undefined" && typeof window.showOpenFilePicker === "function") {
    try {
      const [handle] = await window.showOpenFilePicker({
        multiple: false,
        types: accept ? [{ accept }] : undefined,
      });
      if (!handle) return null;
      const file = await handle.getFile();
      return { name: file.name, content: await file.text() };
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return null;
      // Any other error: fall through to the input fallback.
    }
  }
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    if (accept) input.accept = Object.values(accept).flat().join(",");
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return resolve(null);
      resolve({ name: f.name, content: await f.text() });
    };
    input.oncancel = () => resolve(null);
    input.click();
  });
}

const LS_PREFIX = "quanta-file:";
const IDB_DB = "quanta-workspace";
const IDB_STORE = "kv";
const IDB_KEY = "dir-handle";

// ── tiny IndexedDB kv (persists the directory handle) ──────────
function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(IDB_DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(IDB_STORE);
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
async function idbGet<T>(key: string): Promise<T | undefined> {
  const db = await openIdb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(IDB_STORE, "readonly").objectStore(IDB_STORE).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}
async function idbSet(key: string, value: unknown): Promise<void> {
  const db = await openIdb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
async function idbDel(key: string): Promise<void> {
  const db = await openIdb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function checkPermission(handle: FsDirHandle, request: boolean): Promise<PermState> {
  try {
    const opts: FsPermOpts = { mode: "readwrite" };
    let p = await handle.queryPermission(opts);
    if (p !== "granted" && request) p = await handle.requestPermission(opts);
    return p;
  } catch {
    return "denied";
  }
}

interface WorkspaceState {
  handle: FsDirHandle | null;
  name: string | null;
  supported: boolean;
  permission: PermState;
  files: WsFile[];
  busy: boolean;
  open: () => Promise<void>;
  reconnect: () => Promise<void>;
  close: () => Promise<void>;
  refresh: () => Promise<void>;
  restore: () => Promise<void>;
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  handle: null,
  name: null,
  supported: fsSupported,
  permission: "none",
  files: [],
  busy: false,

  async open() {
    if (!fsSupported || !window.showDirectoryPicker) return;
    try {
      const handle = await window.showDirectoryPicker({ mode: "readwrite" });
      const permission = await checkPermission(handle, true);
      if (permission !== "granted") return;
      await idbSet(IDB_KEY, handle);
      set({ handle, name: handle.name, permission });
      await get().refresh();
    } catch {
      /* user cancelled the picker */
    }
  },

  async reconnect() {
    const handle = get().handle;
    if (!handle) return;
    const permission = await checkPermission(handle, true);
    set({ permission });
    if (permission === "granted") await get().refresh();
  },

  async close() {
    await idbDel(IDB_KEY).catch(() => {});
    set({ handle: null, name: null, permission: "none", files: [] });
  },

  async refresh() {
    set({ busy: true });
    try {
      set({ files: await wsList() });
    } finally {
      set({ busy: false });
    }
  },

  async restore() {
    if (!fsSupported) return;
    try {
      const handle = await idbGet<FsDirHandle>(IDB_KEY);
      if (!handle) return;
      // Don't prompt on load (needs a user gesture); just query current grant.
      const permission = await checkPermission(handle, false);
      set({ handle, name: handle.name, permission });
      if (permission === "granted") await get().refresh();
    } catch {
      /* ignore */
    }
  },
}));

// ── storage primitives used by workspaceIO (FS folder, else localStorage) ──
function usableHandle(): FsDirHandle | null {
  const { handle, permission } = useWorkspaceStore.getState();
  return handle && permission === "granted" ? handle : null;
}

export async function wsWriteText(name: string, content: string): Promise<void> {
  const h = usableHandle();
  if (h) {
    const fh = await h.getFileHandle(name, { create: true });
    const w = await fh.createWritable();
    await w.write(content);
    await w.close();
    void useWorkspaceStore.getState().refresh();
    return;
  }
  try {
    localStorage.setItem(LS_PREFIX + name, content);
  } catch {
    /* quota exceeded: best effort */
  }
}

export async function wsReadText(name: string): Promise<string | null> {
  const h = usableHandle();
  if (h) {
    try {
      const fh = await h.getFileHandle(name);
      return await (await fh.getFile()).text();
    } catch {
      return null;
    }
  }
  return localStorage.getItem(LS_PREFIX + name);
}

export async function wsList(): Promise<WsFile[]> {
  const h = usableHandle();
  if (h) {
    const out: WsFile[] = [];
    for await (const [entryName, entry] of h.entries()) {
      if (entry.kind === "file") {
        try {
          out.push({ name: entryName, size: (await (entry as FsFileHandle).getFile()).size });
        } catch {
          out.push({ name: entryName, size: 0 });
        }
      }
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }
  const out: WsFile[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(LS_PREFIX)) {
      const name = key.slice(LS_PREFIX.length);
      out.push({ name, size: (localStorage.getItem(key) || "").length });
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

export async function wsRemove(name: string): Promise<void> {
  const h = usableHandle();
  if (h) {
    try {
      await h.removeEntry(name);
    } catch {
      /* not found */
    }
    void useWorkspaceStore.getState().refresh();
    return;
  }
  localStorage.removeItem(LS_PREFIX + name);
}

export async function wsExists(name: string): Promise<boolean> {
  const h = usableHandle();
  if (h) {
    try {
      await h.getFileHandle(name);
      return true;
    } catch {
      return false;
    }
  }
  return localStorage.getItem(LS_PREFIX + name) !== null;
}
